import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { PasswordInput } from "@/components/site/PasswordInput";
import { resetPassword } from "@/lib/candidate-api";

export const Route = createFileRoute("/reset-password")({
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search['token'] === "string" && search['token'] ? { token: search['token'] as string } : {},
  head: () => ({
    meta: [
        { name: "robots", content: "noindex,nofollow" },
      { title: "Choose a new password | CLOSDD" },
      { name: "description", content: "Set a new password for your CLOSDD candidate account." },
      { property: "og:title", content: "Choose a new password | CLOSDD" },
      { property: "og:description", content: "Set a new password for your CLOSDD candidate account." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ResetPassword,
});

const inputClass =
  "w-full rounded-full border border-ink/15 bg-white px-5 py-3.5 text-[16px] text-ink outline-none focus:border-purple-strong";

function ResetPassword() {
  const { token } = Route.useSearch();
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  return (
    <div className="min-h-screen bg-cream">
      <main className="mx-auto w-full max-w-[520px] px-5 pb-20 pt-12">
        <Link to="/find-jobs" className="text-[12px] font-bold uppercase tracking-[0.24em] text-purple-strong">
          ← Back to jobs
        </Link>

        {!token ? (
          <>
            <h1 className="mt-5 font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink sm:text-[32px]">
              Invalid reset link
            </h1>
            <p className="mt-3 text-[17px] leading-[1.6] text-ink/65">
              This password reset link is missing or malformed. Request a new one to continue.
            </p>
            <Link
              to="/forgot-password"
              className="mt-8 inline-flex rounded-full bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition hover:-translate-y-0.5"
            >
              Request a new link
            </Link>
          </>
        ) : done ? (
          <>
            <h1 className="mt-5 font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink sm:text-[32px]">
              Password updated
            </h1>
            <p className="mt-3 text-[17px] leading-[1.6] text-ink/65">
              You can now log in with your new password.
            </p>
            <Link
              to="/login"
              className="mt-8 inline-flex rounded-full bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition hover:-translate-y-0.5"
            >
              Go to Log in
            </Link>
          </>
        ) : (
          <>
            <h1 className="mt-5 font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink sm:text-[32px]">
              Choose a new password
            </h1>
            <p className="mt-3 text-[17px] leading-[1.6] text-ink/65">
              Enter a new password for your account.
            </p>

            <form
              className="mt-8 grid gap-5"
              onSubmit={(e) => {
                e.preventDefault();
                if (submitting) return;
                const data = new FormData(e.currentTarget);
                const password = String(data.get("password") ?? "");
                const confirm = String(data.get("confirm") ?? "");
                if (password.length < 8) {
                  setError("Use at least 8 characters.");
                  return;
                }
                if (password !== confirm) {
                  setError("Passwords don't match.");
                  return;
                }
                setError(null);
                setSubmitting(true);
                resetPassword(token, password)
                  .then(() => setDone(true))
                  .catch((err) => setError(err instanceof Error ? err.message : "Something went wrong. Try again."))
                  .finally(() => setSubmitting(false));
              }}
            >
              <label className="grid gap-2">
                <span className="text-[12px] font-bold uppercase tracking-[0.18em] text-ink/50">New password</span>
                <PasswordInput
                  required
                  name="password"
                  placeholder="••••••••"
                  className={`${inputClass} pr-12`}
                />
              </label>
              <label className="grid gap-2">
                <span className="text-[12px] font-bold uppercase tracking-[0.18em] text-ink/50">Confirm password</span>
                <PasswordInput
                  required
                  name="confirm"
                  placeholder="••••••••"
                  className={`${inputClass} pr-12`}
                />
              </label>
              {error ? <p className="text-[14px] font-medium text-purple-strong">{error}</p> : null}
              <button
                type="submit"
                disabled={submitting}
                className="justify-self-start rounded-full bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition hover:-translate-y-0.5 disabled:opacity-60"
              >
                {submitting ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
